import * as z from "zod";
import {
  type DescribeResult,
  type IntentJudgement,
  WordText,
} from "@bindery/composition";

export const DESCRIBE_WORD_PATH = "/api/compose/describe-word";

export const DESCRIBE_MAX_SENTENCE_WORDS = 60;
export const DESCRIBE_MAX_WORD_LENGTH = 40;
// The ladder allows two re-says after the first dictation.
export const DESCRIBE_MAX_FAILED = 3;
export const DESCRIBE_MAX_FAILED_LENGTH = 80;
export const DESCRIBE_MAX_DESCRIPTION_LENGTH = 600;
export const DESCRIBE_MAX_ALTERNATIVES = 3;

export const describeWordRequestSchema = z
  .object({
    /** The sentence without the word being looked for. */
    sentence: z
      .array(WordText.max(DESCRIBE_MAX_WORD_LENGTH))
      .max(DESCRIBE_MAX_SENTENCE_WORDS),
    /** Where the word goes: 0 is before the first word. */
    gapIndex: z.int().min(0),
    /** What recognition heard for the word, each rejected by the child. */
    failedTranscriptions: z
      .array(z.string().trim().min(1).max(DESCRIBE_MAX_FAILED_LENGTH))
      .min(1)
      .max(DESCRIBE_MAX_FAILED),
    /** The child's transcribed description; never added to the composition. */
    description: z.string().trim().min(1).max(DESCRIBE_MAX_DESCRIPTION_LENGTH),
  })
  .refine(({ sentence, gapIndex }) => gapIndex <= sentence.length, {
    message: "the gap must fall inside the sentence",
    path: ["gapIndex"],
  });
export type DescribeWordRequest = z.infer<typeof describeWordRequestSchema>;

export const DescribeWordOutcome = z.enum([
  "candidates",
  "no-candidates",
  "unavailable",
]);
export type DescribeWordOutcome = z.infer<typeof DescribeWordOutcome>;

const Probability = z.number().min(0).max(1);

export const describeWordResponseSchema = z.object({
  outcome: DescribeWordOutcome,
  /** The model that proposed the candidates; null when it did not answer. */
  generator: z.string().min(1).nullable(),
  /** The model that scored them; null when scoring did not answer. */
  scorer: z.string().min(1).nullable(),
  alternatives: z
    .array(z.object({ word: WordText, probability: Probability.nullable() }))
    .max(DESCRIBE_MAX_ALTERNATIVES),
});
export type DescribeWordResponse = z.infer<typeof describeWordResponseSchema>;

export const describeWordErrorSchema = z.object({
  error: z.enum([
    "unauthenticated",
    "invalid_request",
    "method_not_allowed",
    "unconfigured",
  ]),
});
export type DescribeWordError = z.infer<typeof describeWordErrorSchema>;

/** What the child did with the offered words. */
export type DescribeChoice =
  | { readonly kind: "chose"; readonly word: string }
  | { readonly kind: "none-of-these" };

/** The `describe` edit's result, for the choice the child made. */
export const describeResultFor = (
  response: DescribeWordResponse,
  choice: DescribeChoice,
): DescribeResult => {
  const candidates = response.alternatives.map((a) => a.word);
  if (candidates.length === 0) return { outcome: "no-candidates" };
  if (choice.kind === "none-of-these" || !candidates.includes(choice.word)) {
    return { outcome: "none-of-these" };
  }
  return {
    outcome: "chosen",
    to: choice.word,
    model: response.scorer,
    ...(response.generator === null ? {} : { generator: response.generator }),
    candidates,
  };
};

/**
 * The `description-lm` judgement a chosen word carries: what was offered,
 * and the scorer's probability on the word the child chose.
 */
export const describeJudgementFor = (
  response: DescribeWordResponse,
  chosen: string,
  at: number,
): IntentJudgement => ({
  source: "description-lm",
  model: response.scorer,
  generator: response.generator,
  intendedConfidence:
    response.alternatives.find((a) => a.word === chosen)?.probability ?? null,
  alternatives: response.alternatives,
  at,
});
